import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo192.png";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "../constants/urls";

export default function Navbar() {
	const [isOpen, setIsOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	const navItems = [
		{ label: "How it works", id: "features" },
		{ label: "Screenshots", id: "screenshots" },
		{ label: "Sports", id: "sports" },
		{ label: "Blog", href: "/blog" },
	];

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 8);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => {
		if (location.pathname === "/" && location.hash) {
			const sectionId = location.hash.replace("#", "");
			const timeout = setTimeout(() => {
				scrollToSection(sectionId);
			}, 100);
			return () => clearTimeout(timeout);
		}
	}, [location]);

	const scrollToSection = (sectionId) => {
		const element = document.getElementById(sectionId);
		if (element) {
			const offset = 64;
			const elementPosition = element.getBoundingClientRect().top;
			const offsetPosition = elementPosition + window.pageYOffset - offset;

			window.scrollTo({
				top: offsetPosition,
				behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
			});
			setIsOpen(false);
		}
	};

	const handleLogoClick = (e) => {
		e.preventDefault();
		if (location.pathname === "/") {
			window.scrollTo({ top: 0, behavior: "smooth" });
		} else {
			navigate("/");
		}
	};


	return (
		<nav
			aria-label="Main navigation"
			className="fixed top-0 w-full z-50 transition-colors duration-short"
			style={{
				background: "var(--color-paper)",
				borderBottom: `var(--rule-slab) solid var(--color-ink)`,
				boxShadow: scrolled ? "0 4px 0 0 rgba(0,0,0,0.04)" : "none",
			}}
		>
			<div className="container mx-auto px-4 sm:px-6">
				<div className="flex items-center justify-between h-16">
					<a
						href="/"
						onClick={handleLogoClick}
						className="flex items-center gap-2 shrink-0"
					>
						<img src={logo} alt="" width="32" height="32" className="h-8 w-8" />
						<span
							className="font-display font-bold text-xl uppercase"
							style={{ letterSpacing: "var(--tracking-display)", color: "var(--color-ink)" }}
						>
							Racketly
						</span>
					</a>

					<div className="hidden md:flex items-center gap-8">
						{navItems.map((item) => (
							<Link
								key={item.label}
								to={item.href || `/#${item.id}`}
								onClick={() => setIsOpen(false)}
								className="font-display font-bold text-sm uppercase transition-colors duration-short"
								style={{
									letterSpacing: "var(--tracking-label)",
									color: "var(--color-ink-2)",
								}}
								onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
								onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-ink-2)")}
							>
								{item.label}
							</Link>
						))}
						<a
							href={APP_STORE_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="btn-slab btn-slab--fill navbar-store-link"
						>
							iOS
						</a>
						<a
							href={GOOGLE_PLAY_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="btn-slab btn-slab--outline navbar-store-link"
						>
							Android
						</a>
					</div>

					<button
						onClick={() => setIsOpen(!isOpen)}
						className="md:hidden flex items-center justify-center h-9 w-9"
						style={{ border: "var(--rule-fine) solid var(--color-ink)", color: "var(--color-ink)" }}
						aria-label={isOpen ? "Close menu" : "Open menu"}
						aria-expanded={isOpen}
						aria-controls="mobile-navigation"
					>
						<svg
							className="h-5 w-5"
							fill="none"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							{isOpen ? (
								<path d="M6 18L18 6M6 6l12 12" />
							) : (
								<path d="M4 6h16M4 12h16M4 18h16" />
							)}
						</svg>
					</button>
				</div>

				{isOpen && (
					<div
						id="mobile-navigation"
						className="md:hidden mobile-menu-enter mobile-menu-enter-active pb-4"
						style={{ borderTop: "var(--rule-hair) solid var(--color-rule-2)" }}
					>
						<div className="flex flex-col pt-3 gap-1">
							{navItems.map((item) => (
								<Link
									key={item.label}
									to={item.href || `/#${item.id}`}
									onClick={() => setIsOpen(false)}
									className="text-left px-1 py-2 font-display font-bold text-sm uppercase"
									style={{ letterSpacing: "var(--tracking-label)", color: "var(--color-ink-2)" }}
								>
									{item.label}
								</Link>
							))}
							<a
								href={APP_STORE_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="btn-slab btn-slab--fill mt-2 w-full"
							>
								Download for iOS
							</a>
							<a
								href={GOOGLE_PLAY_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="btn-slab btn-slab--outline"
							>
								Download for Android
							</a>
						</div>
					</div>
				)}
			</div>
		</nav>
	);
}
